import '../screens/Vorgang/Unterschriften.css';
import React, { ChangeEvent, useState, type JSX } from 'react';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { SPACING_BETWEEN_FORM_FIELDS } from './common/spacings';
import { Signature } from './common/Signature';
import { useDateiUrl } from '../hooks/useDateiUrl';
import { Vorgang } from '../types';
import { Unterzeichner } from '../shared/constants';
import { Modal } from './Modal';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { UnterschriftenCheckboxState } from '../screens/Vorgang/Unterschriften';

type Props = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly updateCheckboxState: (newState: Partial<UnterschriftenCheckboxState>) => void;
  readonly checkboxState: UnterschriftenCheckboxState;
};

export function DSGVOv2({ vorgang, isLoading, updateCheckboxState, checkboxState }: Props): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Grid size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checkboxState.dsgvoV2}
              onChange={(event: ChangeEvent<HTMLInputElement>) => updateCheckboxState({ dsgvoV2: event.target.checked })}
              name="DSGVO"
              color="primary"
              data-testid="dsgvoCheckbox"
            />
          }
          label={
            <>
              Ich habe die{' '}
              <Link
                onClick={(event) => {
                  event.preventDefault();
                  setIsModalOpen(true);
                }}
                data-testid="dsgvoLink"
              >
                Datenschutzerklärung
              </Link>{' '}
              erhalten.*
            </>
          }
        />
      </Grid>
      <DSGVOModal vorgang={vorgang} isLoading={isLoading} isModalOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}

type ModalProps = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly isModalOpen: boolean;
  readonly onClose: () => void;
};

function DSGVOModal({ vorgang, isLoading, isModalOpen, onClose }: ModalProps): JSX.Element {
  const dsgvoUrl = useDateiUrl(vorgang?.unterschriften?.dsgvoV2?.key);

  const signDate = vorgang?.unterschriften?.dsgvoV2?.datum ?? new Date().toISOString();

  return (
    <Modal
      openModal={isModalOpen}
      setOpenModal={onClose}
      title="Erklärung zur DSGVO (Datenschutzgrundverordnung)"
      body={
        <Grid container spacing={SPACING_BETWEEN_FORM_FIELDS} data-testid="dsgvoModal">
          <Grid size={12}>
            <Typography variant="body2">
              Ich stimme ausdrücklich zu, dass alle Daten, die mit meiner Person (Auftraggeber/Fahrzeughalter) und dem Auftrag zur Gutachten- und
              Reparaturnachweiserstellung im Zusammenhang stehen, wie z.B. Daten zu meiner Person, des zu begutachtenden Objektes und Daten zur Ermittlung des
              Versicherers, verwendet werden dürfen.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Ich wurde darauf hingewiesen, dass meine Daten von <i>Experts24 Tassone GmbH</i> entsprechend der EU-DSGVO geschützt werden.
              <br /> Außerdem werden meine Daten an Dritte weitergegeben, wenn dies zur Leistungserstellung notwendig ist.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">Dritte sind:</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              <ul>
                <li>Fa. DAT (Datenlieferant für Fahrzeuge)</li>
                <li>GDV (Gesamtverband der Deutschen Versicherer)</li>
                <li>Händler/Autohäuser/Reparaturbetriebe zur Ermittlung von Restwert/Wiederbeschaffungswert</li>
                <li>Versicherer, die im Schadensregulierungsverfahren involviert sind</li>
                <li>Steuerberater/Rechtsanwälte</li>
              </ul>
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Die Übermittlung per E-Mail erfolgt ohne Sicherheitsmaßnahmen und ohne eine separate Verschlüsselung. Dadurch können die Daten ggf. durch Dritte
              <br />
              abgefangen und gelesen werden. Ich kann meiner Einwilligung jederzeit mit Wirkung für die Zukunft gegenüber <i>Experts24 Tassone GmbH</i>{' '}
              widersprechen.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Signature
              onSign={() => {}}
              onDeleteSignature={() => {}}
              signatureUrl={dsgvoUrl}
              isLoading={isLoading}
              unterzeichnerAuswahl={vorgang?.unterschriften?.dsgvoV2?.unterzeichner ?? Unterzeichner.AUFTRAGGEBER}
              vorgang={vorgang}
              date={signDate}
              readOnly={true}
            />
          </Grid>
        </Grid>
      }
      footer={
        <div className="unterschriften__modal-button">
          <Button color="primary" variant="contained" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    />
  );
}
